import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Clock, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { AGENCY } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IS_STATIC = process.env.REACT_APP_STATIC_BUILD === "true";

const inputCls =
  "w-full border border-[#2A303A] bg-[#101318] px-4 py-3 text-sm text-[#F2F4F7] focus:outline-none focus:border-[#2E5BFF] focus:ring-2 focus:ring-[#2E5BFF]/30 placeholder:text-[#5B6470]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", company: "", website_url: "", phone: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const err = {};
    if (form.name.trim().length < 2) err.name = "Bitte geben Sie Ihren Namen an.";
    if (form.phone.trim().length < 5) err.phone = "Bitte geben Sie Ihre Telefonnummer an.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Bitte geben Sie eine gültige E-Mail-Adresse an.";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      if (IS_STATIC) {
        await axios.post("/", new URLSearchParams({ "form-name": "analyse-anfrage", ...form }), {
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        });
      } else {
        await axios.post(`${API}/leads`, form);
      }
      setSent(true);
      toast.success("Vielen Dank! Ihre Anfrage ist eingegangen.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 md:py-32 px-6 md:px-12 bg-[#171B21] border-t border-[#2A303A]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2E5BFF] mb-4">Kontakt</p>
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
          Kostenlose Analyse anfordern
        </h2>
        <p className="mt-5 text-base md:text-lg text-[#9AA3AF] max-w-2xl leading-relaxed">
          Unverbindlich und ehrlich: Sie bekommen eine kurze Einschätzung Ihrer Web-Präsenz und einen konkreten Vorschlag, was sich lohnt.
        </p>

        <div className="mt-14 grid lg:grid-cols-[1.4fr_1fr] gap-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="bg-[#101318] border border-[#2A303A] p-6 md:p-10"
          >
            {sent ? (
              <div className="flex flex-col items-center text-center py-16" data-testid="contact-success-message">
                <CheckCircle2 className="w-14 h-14 text-[#2E5BFF] mb-5" />
                <h3 className="font-display text-2xl md:text-3xl font-semibold">Anfrage erhalten!</h3>
                <p className="mt-3 text-[#9AA3AF] max-w-sm leading-relaxed">
                  Vielen Dank, {form.name.split(" ")[0]}. Sie hören innerhalb von 24 Stunden an Werktagen von mir.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="cf-name">Name *</label>
                    <input id="cf-name" className={inputCls} placeholder="Max Mustermann" value={form.name} onChange={set("name")} data-testid="contact-input-name" />
                    {errors.name && <p className="text-xs text-red-400 mt-1.5" data-testid="contact-error-name">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="cf-company">Firma</label>
                    <input id="cf-company" className={inputCls} placeholder="Mustermann GmbH" value={form.company} onChange={set("company")} data-testid="contact-input-company" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold mb-2" htmlFor="cf-url">Aktuelle Website-URL (falls vorhanden)</label>
                    <input id="cf-url" type="url" className={inputCls} placeholder="https://www.ihre-website.de" value={form.website_url} onChange={set("website_url")} data-testid="contact-input-website" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="cf-phone">Telefon *</label>
                    <input id="cf-phone" type="tel" className={inputCls} placeholder="040 123 456 78" value={form.phone} onChange={set("phone")} data-testid="contact-input-phone" />
                    {errors.phone && <p className="text-xs text-red-400 mt-1.5" data-testid="contact-error-phone">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="cf-email">E-Mail *</label>
                    <input id="cf-email" type="email" className={inputCls} placeholder="max@beispiel.de" value={form.email} onChange={set("email")} data-testid="contact-input-email" />
                    {errors.email && <p className="text-xs text-red-400 mt-1.5" data-testid="contact-error-email">{errors.email}</p>}
                  </div>
                </div>
                <div className="mt-5">
                  <label className="block text-sm font-semibold mb-2" htmlFor="cf-msg">Kurze Nachricht zum Vorhaben</label>
                  <textarea id="cf-msg" rows={4} className={inputCls} placeholder="Worum geht es? Neue Website, Relaunch, Beratung?" value={form.message} onChange={set("message")} data-testid="contact-input-message" />
                </div>
                <button type="submit" disabled={sending} className="btn-primary px-8 py-4 text-base font-semibold w-full sm:w-auto mt-8 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit-button">
                  {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                  {sending ? "Wird gesendet, einen Moment" : AGENCY.cta}
                </button>
                <p className="text-xs text-[#5B6470] mt-4">* Pflichtfelder. Ihre Daten werden ausschließlich zur Bearbeitung Ihrer Anfrage verwendet.</p>
              </form>
            )}
          </motion.div>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="space-y-6"
          >
            <div className="bg-[#101318] border border-[#2A303A] p-8">
              <p className="text-xs uppercase tracking-widest text-[#5B6470] mb-3">Direkter Kontakt</p>
              <a href={`mailto:${AGENCY.email}`} className="font-display text-lg md:text-xl font-semibold hover:text-[#2E5BFF] transition-colors inline-flex items-center gap-3 break-words [overflow-wrap:anywhere]" data-testid="contact-email-link">
                <Mail className="w-5 h-5 shrink-0" /> {AGENCY.email}
              </a>
              <div className="mt-6 flex items-start gap-3 text-sm text-[#9AA3AF]">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" /> Antwort innerhalb von 24 Stunden an Werktagen
              </div>
              <div className="mt-3 flex items-start gap-3 text-sm text-[#9AA3AF]">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {AGENCY.name}, {AGENCY.region}
              </div>
            </div>
            <div className="border border-[#2A303A] p-8">
              <p className="font-display text-lg font-semibold">Was Sie von der Analyse haben</p>
              <ul className="mt-4 space-y-3 text-sm text-[#9AA3AF]">
                <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#2E5BFF] mt-0.5 shrink-0" /> Ehrliche Einschätzung Ihrer aktuellen Web-Präsenz</li>
                <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#2E5BFF] mt-0.5 shrink-0" /> Konkrete Verbesserungsvorschläge, verständlich erklärt</li>
                <li className="flex gap-2.5"><CheckCircle2 className="w-4 h-4 text-[#2E5BFF] mt-0.5 shrink-0" /> Festpreis-Angebot, wenn Sie es wünschen. Keine Verpflichtung.</li>
              </ul>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
};
